import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ShieldBan, Clock, Globe, Mail, User, Briefcase, Linkedin, Hash, MessageSquare, Database, ExternalLink } from 'lucide-react'

export interface BlocklistEntry {
  id: string
  created_at: string
  updated_at: string
  email: string | null
  domain: string | null
  name: string | null
  first_name: string | null
  last_name: string | null
  job_title: string | null
  linkedin: string | null
  prospect_id: string | null
  lead_id: string | null
  campaign_id: number | null
  campaign_name: string | null
  channel_type: string | null
  client_id: string | null
  workspace_name: string | null
  reply_id: string | null
  reply_category_type: string | null
  label_name: string | null
  reply_count: number | null
  reply_text: string | null
  reply_subject: string | null
  categorization_reasoning: string | null
  block_type: string
  block_reason: string | null
  smartlead_sync_status: string | null
  smartlead_sync_error: string | null
  smartlead_sync_attempted_at: string | null
  expires_at: string | null
  expired: boolean
  removed_from_smartlead: boolean
  removed_at: string | null
  // CRM-sourced fields (populated when entry originated from a CRM list via Clay)
  external_source: string | null
  external_id: string | null
  external_url: string | null
  external_metadata: Record<string, any> | null
  sync_retry_count: number | null
}

// Display config for each block_type. CRM types use domain-only styling.
export const BLOCK_TYPE_META: Record<string, { label: string; icon: string; color: string }> = {
  email_and_domain:   { label: 'Email + Domain',        icon: '🔒', color: 'bg-red-50 text-red-700 border-red-200' },
  email_only:         { label: 'Email Only',            icon: '✉️', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  domain_only:        { label: 'Domain Only',           icon: '🌐', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  crm_customer:       { label: 'CRM — Active Customer', icon: '👥', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  crm_churned:        { label: 'CRM — Former Customer', icon: '📤', color: 'bg-slate-50 text-slate-700 border-slate-200' },
  crm_opportunity:    { label: 'CRM — Opportunity',     icon: '🎯', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  crm_other_division: { label: 'CRM — Other Division',  icon: '🏢', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  crm_meeting_booked: { label: 'CRM — Meeting Booked',  icon: '📅', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  crm_blacklist:      { label: 'CRM — Blacklist',       icon: '🚫', color: 'bg-rose-50 text-rose-700 border-rose-200' },
}

export function getBlockTypeMeta(type: string | null | undefined) {
  if (!type) return { label: 'Unknown', icon: '❓', color: 'bg-gray-50 text-gray-700 border-gray-200' }
  return BLOCK_TYPE_META[type] || { label: type, icon: '•', color: 'bg-gray-50 text-gray-700 border-gray-200' }
}

export function isCrmSourced(entry: Pick<BlocklistEntry, 'external_source'>): boolean {
  return !!entry.external_source
}

export function getDisplayName(entry: Pick<BlocklistEntry, 'name' | 'external_metadata' | 'domain'>): string {
  if (entry.name && entry.name.trim()) return entry.name
  const accountName = entry.external_metadata?.account_name
  if (accountName && String(accountName).trim()) return String(accountName)
  return entry.domain || 'Unknown'
}

interface BlocklistDetailDialogProps {
  entry: BlocklistEntry | null
  isOpen: boolean
  onClose: () => void
}

export function BlocklistDetailDialog({ entry, isOpen, onClose }: BlocklistDetailDialogProps) {
  if (!entry || !isOpen) return null

  const getCategoryColor = (type: string | null) => {
    if (!type) return ''
    const t = type.toLowerCase()
    if (t === 'positive') return 'bg-green-100 text-green-800 border-green-300'
    if (t === 'negative') return 'bg-red-100 text-red-800 border-red-300'
    if (t === 'ooo_bounced') return 'bg-purple-100 text-purple-800 border-purple-300'
    return 'bg-gray-100 text-gray-800 border-gray-300'
  }

  const fromCrm = isCrmSourced(entry)
  const displayName = getDisplayName(entry)
  const blockTypeMeta = getBlockTypeMeta(entry.block_type)

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl w-[90vw] h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ShieldBan className="w-5 h-5 text-red-500" />
            Blocklist Entry Details
          </DialogTitle>
          <DialogDescription>
            Full details for this blocklist record
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6">
            {/* Lead Information */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-4 h-4" />
                {fromCrm ? 'Account Information' : 'Lead Information'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">
                    {fromCrm ? 'Account Name' : 'Full Name'}
                  </div>
                  <div className="text-sm font-medium">{displayName}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Email</div>
                  <div className="text-sm font-medium flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                    {entry.email || <span className="text-muted-foreground italic">N/A (domain-only)</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Domain</div>
                  <div className="text-sm font-medium flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                    {entry.domain || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Job Title</div>
                  <div className="text-sm font-medium flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />
                    {entry.job_title || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">LinkedIn</div>
                  <div className="text-sm font-medium">
                    {entry.linkedin ? (
                      <a
                        href={entry.linkedin.startsWith('http') ? entry.linkedin : `https://www.linkedin.com/in/${entry.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Linkedin className="w-3.5 h-3.5" />
                        {entry.linkedin}
                      </a>
                    ) : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Prospect ID</div>
                  <div className="text-sm font-medium font-mono text-xs">{entry.prospect_id || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Lead ID</div>
                  <div className="text-sm font-medium font-mono text-xs">{entry.lead_id || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Block Configuration */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShieldBan className="w-4 h-4" />
                Block Configuration
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Block Type:</span>
                  <Badge variant="outline" className={blockTypeMeta.color}>
                    {blockTypeMeta.icon} {blockTypeMeta.label}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Block Reason:</span>
                  <span className="font-medium text-sm text-right max-w-[60%]">{entry.block_reason || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <Badge variant="outline" className={
                    entry.expired
                      ? 'bg-gray-100 text-gray-600 border-gray-300'
                      : 'bg-green-100 text-green-800 border-green-300'
                  }>
                    {entry.expired ? '♻️ Expired / Removed' : '🟢 Active'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expiry:</span>
                  <span className="font-medium">
                    {entry.expires_at
                      ? new Date(entry.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                      : '🔐 Permanent (never expires)'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Blocked At:</span>
                  <span className="font-medium">
                    {new Date(entry.created_at).toLocaleString()}
                  </span>
                </div>
                {entry.removed_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Removed At:</span>
                    <span className="font-medium">
                      {new Date(entry.removed_at).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* SmartLead Sync */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">SmartLead Sync</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Sync Status:</span>
                  <span className={`font-medium ${
                    entry.smartlead_sync_status === 'success' ? 'text-green-600' :
                    entry.smartlead_sync_status === 'failed' ? 'text-red-600' :
                    entry.smartlead_sync_status === 'already_blocked' ? 'text-yellow-600' :
                    'text-gray-500'
                  }`}>
                    {entry.smartlead_sync_status === 'success' ? '✅ Synced' :
                     entry.smartlead_sync_status === 'failed' ? '❌ Failed' :
                     entry.smartlead_sync_status === 'already_blocked' ? '⏭️ Already Blocked' :
                     entry.smartlead_sync_status === 'pending' ? '⏳ Pending' :
                     entry.smartlead_sync_status || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sync Attempted At:</span>
                  <span className="font-medium">
                    {entry.smartlead_sync_attempted_at
                      ? new Date(entry.smartlead_sync_attempted_at).toLocaleString()
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Removed from SmartLead:</span>
                  <span className="font-medium">
                    {entry.removed_from_smartlead ? '✅ Yes' : '—'}
                  </span>
                </div>
                {entry.smartlead_sync_error && (
                  <div className="flex flex-col gap-1 pt-2 border-t">
                    <span className="text-gray-600 text-sm">Sync Error:</span>
                    <span className="text-red-600 text-xs font-mono bg-red-50 p-2 rounded break-all">
                      {entry.smartlead_sync_error}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* CRM Source (only shown for CRM-sourced blocks) */}
            {fromCrm && (
              <div className="bg-white border rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  CRM Source
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground">Source System</div>
                    <div className="text-sm font-medium capitalize">{entry.external_source || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">External Record ID</div>
                    <div className="text-sm font-medium font-mono text-xs">{entry.external_id || 'N/A'}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-xs text-muted-foreground">External Record Link</div>
                    {entry.external_url ? (
                      <a
                        href={entry.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1 break-all"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open in CRM
                      </a>
                    ) : (
                      <div className="text-sm text-muted-foreground">N/A</div>
                    )}
                  </div>
                </div>
                {entry.external_metadata && Object.keys(entry.external_metadata).length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="text-xs text-muted-foreground mb-2">Metadata</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {Object.entries(entry.external_metadata).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</div>
                          <div className="text-sm font-medium break-words">
                            {value == null || value === '' ? <span className="text-muted-foreground italic">—</span> : String(value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reply Context (only shown for reply-driven blocks) */}
            {!fromCrm && (
            <div className="bg-white border rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Reply Context
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">Category</div>
                  <div className="mt-1">
                    {entry.reply_category_type ? (
                      <Badge variant="outline" className={getCategoryColor(entry.reply_category_type)}>
                        {entry.reply_category_type}
                      </Badge>
                    ) : <span className="text-sm text-gray-500">N/A</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Label</div>
                  <div className="text-sm font-medium mt-1">{entry.label_name || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Reply Count</div>
                  <div className="text-sm font-medium mt-1">
                    {entry.reply_count != null ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        Reply #{entry.reply_count}
                      </Badge>
                    ) : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Channel Type</div>
                  <div className="text-sm font-medium mt-1">{entry.channel_type || 'N/A'}</div>
                </div>
              </div>

              {entry.reply_subject && (
                <div className="mt-4 pt-4 border-t">
                  <div className="text-xs text-muted-foreground mb-1">Reply Subject</div>
                  <div className="text-sm font-medium">{entry.reply_subject}</div>
                </div>
              )}

              {entry.reply_text && (
                <div className="mt-4 pt-4 border-t">
                  <div className="text-xs text-muted-foreground mb-1">Reply Text</div>
                  <div className="text-sm bg-gray-50 p-3 rounded max-h-[200px] overflow-y-auto whitespace-pre-wrap break-words">
                    {entry.reply_text}
                  </div>
                </div>
              )}

              {entry.categorization_reasoning && (
                <div className="mt-4 pt-4 border-t">
                  <div className="text-xs text-muted-foreground mb-1">AI Categorization Reasoning</div>
                  <div className="text-sm bg-blue-50 p-3 rounded max-h-[150px] overflow-y-auto whitespace-pre-wrap break-words">
                    {entry.categorization_reasoning}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Campaign & Client Context */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Campaign & Client</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">Campaign Name</div>
                  <div className="text-sm font-medium">{entry.campaign_name || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Campaign ID</div>
                  <div className="text-sm font-medium">{entry.campaign_id != null ? entry.campaign_id : 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Workspace</div>
                  <div className="text-sm font-medium">{entry.workspace_name || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Client ID</div>
                  <div className="text-sm font-medium font-mono text-xs">{entry.client_id || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Reply ID</div>
                  <div className="text-sm font-medium font-mono text-xs">{entry.reply_id || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Entry ID</div>
                  <div className="text-sm font-medium font-mono text-xs">{entry.id}</div>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
